import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { money, timeAgo } from "@/lib/format";
import {
  PageHeader,
  Loader,
  EmptyState,
  VegDot,
} from "@/components/common/Bits";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  CheckCircle2,
  XCircle,
  Clock,
  ShieldAlert,
  RefreshCw,
  ShoppingBag,
  Truck,
  UtensilsCrossed,
  Phone,
  MapPin,
  CreditCard,
  PlusCircle,
} from "lucide-react";
import { toast } from "sonner";

const REASONS = [
  "Item unavailable",
  "Wrong quantity",
  "Customer changed order",
  "Pricing issue",
  "Other",
];

const ORDER_TYPES = {
  dine_in: {
    label: "Dine-in",
    icon: UtensilsCrossed,
    className: "bg-blue-50 text-blue-700 border-blue-200",
  },
  takeaway: {
    label: "Takeaway",
    icon: ShoppingBag,
    className: "bg-amber-50 text-amber-700 border-amber-200",
  },
  delivery: {
    label: "Delivery",
    icon: Truck,
    className: "bg-emerald-50 text-emerald-700 border-emerald-200",
  },
};

const PAYMENT_LABELS = {
  paid: "Paid",
  pending: "Pending",
  failed: "Failed",
  refunded: "Refunded",
};

function getOrderType(order) {
  const raw = String(
    order?.type || order?.order_type || order?.source || "dine_in",
  ).toLowerCase();

  if (raw.includes("delivery")) return "delivery";

  if (raw.includes("takeaway") || raw.includes("take_away")) {
    return "takeaway";
  }

  return "dine_in";
}

function formatPaymentStatus(status) {
  const key = String(status || "pending").toLowerCase();
  return PAYMENT_LABELS[key] || key.replace("_", " ");
}

export default function Approvals() {
  const { branchId, subscribe } = useApp();
  const { user } = useAuth();

  const [orders, setOrders] = useState(null);
  const [additions, setAdditions] = useState([]);

  const [reject, setReject] = useState(null);
  const [reason, setReason] = useState(REASONS[0]);

  const [loadingAction, setLoadingAction] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const activeBranch = branchId === "all" ? null : branchId;

  /*
   * ---------------------------------------------------------
   * LOAD PENDING ORDERS + PENDING CUSTOMER ADDITIONS
   * ---------------------------------------------------------
   */

  const load = useCallback(
    async (showRefresh = false) => {
      if (showRefresh) {
        setRefreshing(true);
      }

      try {
        const orderParams = {
          approval_status: "pending",
          limit: 100,
        };

        const additionParams = {
          status: "pending",
          limit: 100,
        };

        if (activeBranch) {
          orderParams.branch_id = activeBranch;
          additionParams.branch_id = activeBranch;
        }

        const [ordersResponse, additionsResponse] = await Promise.all([
          api.orders(orderParams),
          api.orderAdditions(additionParams),
        ]);

        setOrders(
          ordersResponse?.data?.items ||
            (Array.isArray(ordersResponse?.data) ? ordersResponse.data : []),
        );

        setAdditions(
          Array.isArray(additionsResponse?.data) ? additionsResponse.data : [],
        );
      } catch (error) {
        console.error("Approval load error:", error);

        /*
         * Keep the page usable even if one request
         * fails unexpectedly.
         */
        if (!orders) {
          setOrders([]);
        }

        if (showRefresh) {
          toast.error(
            error?.response?.data?.detail || "Failed to refresh approvals",
          );
        }
      } finally {
        setRefreshing(false);
      }
    },
    [activeBranch],
  );

  useEffect(() => {
    setOrders(null);
    setAdditions([]);
    load();
  }, [load]);

  /*
   * ---------------------------------------------------------
   * REALTIME
   * ---------------------------------------------------------
   *
   * order.new / order.update
   *     -> normal approval refresh
   *
   * order.addition / order.addition.update
   *     -> customer Order More refresh
   */

  useEffect(() => {
    const events = [
      "order.new",
      "order.update",
      "order.addition",
      "order.addition.update",
    ];

    const subscriptions = events.map((event) => subscribe(event, () => load()));

    return () => subscriptions.forEach((unsubscribe) => unsubscribe());
  }, [subscribe, load]);

  /*
   * ---------------------------------------------------------
   * NORMAL ORDER APPROVE
   * ---------------------------------------------------------
   */

  const approve = async (order) => {
    if (!user?.can_approve) {
      toast.error("You are not authorized to approve orders");
      return;
    }

    setLoadingAction(`approve-${order.id}`);

    try {
      await api.approveOrder(order.id);

      toast.success(`Order #${order.order_no} approved and sent to kitchen`);

      await load();
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
          error?.response?.data?.message ||
          "Failed to approve order",
      );
    } finally {
      setLoadingAction(null);
    }
  };

  /*
   * ---------------------------------------------------------
   * NORMAL ORDER REJECT
   * ---------------------------------------------------------
   */

  const doReject = async () => {
    if (!reject) return;

    if (!user?.can_approve) {
      toast.error("You are not authorized to reject orders");
      return;
    }

    const isAddition = reject.kind === "addition";

    const loadingKey = isAddition
      ? `reject-addition-${reject.id}`
      : `reject-${reject.id}`;

    setLoadingAction(loadingKey);

    try {
      if (isAddition) {
        await api.rejectOrderAddition(reject.order_id, reject.id, reason);

        toast.success(
          `Additional items for Order #${reject.order_no} rejected`,
        );
      } else {
        await api.rejectOrder(reject.id, reason);

        toast.success(`Order #${reject.order_no} rejected`);
      }

      setReject(null);
      setReason(REASONS[0]);

      await load();
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
          error?.response?.data?.message ||
          (isAddition
            ? "Failed to reject additional items"
            : "Failed to reject order"),
      );
    } finally {
      setLoadingAction(null);
    }
  };

  /*
   * ---------------------------------------------------------
   * CUSTOMER ADDITION APPROVE
   * ---------------------------------------------------------
   */

  const approveAddition = async (addition) => {
    if (!user?.can_approve) {
      toast.error("You are not authorized to approve orders");
      return;
    }

    setLoadingAction(`approve-addition-${addition.id}`);

    try {
      await api.approveOrderAddition(addition.order_id, addition.id);

      toast.success(
        `Additional items for Order #${addition.order_no} approved and sent to kitchen`,
      );

      await load();
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
          error?.response?.data?.message ||
          "Failed to approve additional items",
      );
    } finally {
      setLoadingAction(null);
    }
  };

  /*
   * ---------------------------------------------------------
   * SUMMARY
   * ---------------------------------------------------------
   */

  const summary = useMemo(() => {
    const orderList = orders || [];
    const additionList = additions || [];

    return {
      totalOrders: orderList.length,
      totalAdditions: additionList.length,

      total: orderList.length + additionList.length,

      takeaway: orderList.filter((o) => getOrderType(o) === "takeaway").length,

      delivery: orderList.filter((o) => getOrderType(o) === "delivery").length,

      dineIn: orderList.filter((o) => getOrderType(o) === "dine_in").length,
    };
  }, [orders, additions]);

  if (!orders) {
    return (
      <div className="p-6">
        <Loader label="Loading approvals" />
      </div>
    );
  }

  const hasAnythingPending = orders.length > 0 || additions.length > 0;

  return (
    <div className="p-4 sm:p-6 animate-fade-up">
      <PageHeader
        title="Order Approvals"
        subtitle="Review and authorize orders and additional items before they reach the kitchen"
        actions={
          <Button
            variant="outline"
            onClick={() => load(true)}
            disabled={refreshing}
            className="rounded-xl font-semibold"
            data-testid="approval-refresh"
          >
            <RefreshCw
              size={16}
              className={`mr-1.5 ${refreshing ? "animate-spin" : ""}`}
            />
            Refresh
          </Button>
        }
      />

      {/* Permission warning */}
      {!user?.can_approve && (
        <div
          className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-700 font-medium mb-5 flex items-center gap-2"
          data-testid="approve-not-authorized"
        >
          <ShieldAlert size={17} />

          <span>
            Your role cannot approve orders. Ask an owner, manager or
            supervisor.
          </span>
        </div>
      )}

      {/* Summary */}
      {hasAnythingPending && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
          <SummaryCard
            label="Pending Orders"
            value={summary.totalOrders}
            icon={Clock}
          />

          <SummaryCard
            label="Additions"
            value={summary.totalAdditions}
            icon={PlusCircle}
          />

          <SummaryCard
            label="Dine-in"
            value={summary.dineIn}
            icon={UtensilsCrossed}
          />

          <SummaryCard
            label="Takeaway"
            value={summary.takeaway}
            icon={ShoppingBag}
          />

          <SummaryCard label="Delivery" value={summary.delivery} icon={Truck} />
        </div>
      )}

      {!hasAnythingPending ? (
        <EmptyState
          icon={CheckCircle2}
          title="All caught up"
          subtitle="No orders or additional items are waiting for approval right now."
        />
      ) : (
        <div className="space-y-8">
          {/* =================================================
              NORMAL ORDER APPROVALS
          ================================================== */}

          {orders.length > 0 && (
            <section>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h2 className="text-base font-bold">Order Approvals</h2>

                  <p className="text-xs text-muted-foreground mt-0.5">
                    New orders waiting for approval
                  </p>
                </div>

                <span className="rounded-full bg-primary/10 text-primary px-2.5 py-1 text-xs font-bold">
                  {orders.length}
                </span>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                {orders.map((order) => {
                  const type = getOrderType(order);
                  const typeInfo = ORDER_TYPES[type];

                  const TypeIcon = typeInfo?.icon || UtensilsCrossed;

                  const isApproving = loadingAction === `approve-${order.id}`;

                  const isRejecting = loadingAction === `reject-${order.id}`;

                  const customerName =
                    order.customer_name || order.customer?.name || "";

                  const customerPhone =
                    order.customer_phone || order.customer?.phone || "";

                  const deliveryAddress =
                    order.delivery_address ||
                    order.address ||
                    order.customer?.address ||
                    "";

                  return (
                    <div
                      key={order.id}
                      className="rounded-2xl bg-card border border-border p-5 shadow-[0_2px_8px_rgba(28,25,23,0.04)] hover:shadow-[0_8px_24px_rgba(28,25,23,0.08)] transition-shadow"
                      data-testid={`approval-${order.id}`}
                    >
                      {/* Header */}
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-display text-lg font-bold">
                              #{order.order_no}
                            </span>

                            <span
                              className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${typeInfo.className}`}
                            >
                              <TypeIcon size={11} />
                              {typeInfo.label}
                            </span>
                          </div>

                          <p className="text-xs text-muted-foreground mt-1">
                            {order.table_name
                              ? `Table ${order.table_name}`
                              : customerName || "Walk-in customer"}
                          </p>
                        </div>

                        <div className="flex flex-col items-end gap-1.5 shrink-0">
                          <span className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Clock size={12} />
                            {timeAgo(order.created_at)}
                          </span>

                          {order.source && (
                            <StatusBadge status={order.source} />
                          )}
                        </div>
                      </div>

                      {/* Customer */}
                      {(customerName || customerPhone) && (
                        <div className="mt-3 rounded-xl bg-secondary/40 border border-border px-3 py-2.5">
                          <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              {customerName && (
                                <p className="text-sm font-semibold truncate">
                                  {customerName}
                                </p>
                              )}

                              {customerPhone && (
                                <p className="text-xs text-muted-foreground flex items-center gap-1.5 mt-0.5">
                                  <Phone size={11} />
                                  {customerPhone}
                                </p>
                              )}
                            </div>

                            {customerPhone && (
                              <a
                                href={`tel:${customerPhone}`}
                                onClick={(e) => e.stopPropagation()}
                                className="h-8 w-8 rounded-lg bg-white border border-border grid place-items-center text-primary hover:bg-primary/5"
                                aria-label={`Call ${
                                  customerName || "customer"
                                }`}
                              >
                                <Phone size={14} />
                              </a>
                            )}
                          </div>

                          {type === "delivery" && deliveryAddress && (
                            <div className="flex items-start gap-1.5 mt-2 pt-2 border-t border-border text-xs text-muted-foreground">
                              <MapPin size={12} className="mt-0.5 shrink-0" />

                              <span>{deliveryAddress}</span>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Items */}
                      <div className="mt-3 border border-border rounded-xl divide-y overflow-hidden">
                        {Array.isArray(order.items) &&
                          order.items.map((item, index) => (
                            <div
                              key={item.id || item.menu_item_id || index}
                              className="flex items-center gap-2 px-3 py-2 text-sm"
                            >
                              <VegDot
                                veg={item.veg}
                                className="h-3.5 w-3.5 shrink-0"
                              />

                              <span className="flex-1 font-medium leading-tight">
                                {item.name}
                              </span>

                              <span className="text-muted-foreground font-medium">
                                ×{item.qty}
                              </span>

                              {item.price != null && (
                                <span className="font-semibold min-w-[60px] text-right">
                                  {money(
                                    Number(item.price) * Number(item.qty || 1),
                                  )}
                                </span>
                              )}
                            </div>
                          ))}
                      </div>

                      {/* Note */}
                      {order.note && (
                        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-2 mt-2">
                          <span className="font-bold">Note:</span> {order.note}
                        </div>
                      )}

                      {/* Footer */}
                      <div className="flex items-center justify-between gap-3 mt-4">
                        <div>
                          <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">
                            Order Total
                          </p>

                          <p className="font-display text-xl font-bold mt-0.5">
                            {money(order.total)}
                          </p>
                        </div>

                        <div className="flex flex-col items-end gap-1">
                          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                            <CreditCard size={12} />

                            {formatPaymentStatus(order.payment_status)}
                          </div>

                          <StatusBadge
                            status={order.payment_status || "pending"}
                          />
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="grid grid-cols-2 gap-2 mt-4">
                        <Button
                          variant="outline"
                          onClick={() => {
                            setReject({
                              ...order,
                              kind: "order",
                            });

                            setReason(REASONS[0]);
                          }}
                          disabled={!user?.can_approve || !!loadingAction}
                          className="h-10 rounded-xl font-semibold text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                          data-testid={`reject-${order.id}`}
                        >
                          <XCircle size={16} className="mr-1.5" />

                          {isRejecting ? "Rejecting..." : "Reject"}
                        </Button>

                        <Button
                          onClick={() => approve(order)}
                          disabled={!user?.can_approve || !!loadingAction}
                          className="h-10 rounded-xl font-semibold"
                          data-testid={`approve-${order.id}`}
                        >
                          {isApproving ? (
                            <>
                              <RefreshCw
                                size={16}
                                className="mr-1.5 animate-spin"
                              />
                              Approving...
                            </>
                          ) : (
                            <>
                              <CheckCircle2 size={16} className="mr-1.5" />
                              Approve
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* =================================================
              CUSTOMER ADDITION APPROVALS
          ================================================== */}

          {additions.length > 0 && (
            <section>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h2 className="text-base font-bold flex items-center gap-2">
                    <PlusCircle size={18} className="text-primary" />
                    Additional Item Approvals
                  </h2>

                  <p className="text-xs text-muted-foreground mt-0.5">
                    Customer requested additional items for existing orders
                  </p>
                </div>

                <span className="rounded-full bg-primary/10 text-primary px-2.5 py-1 text-xs font-bold">
                  {additions.length}
                </span>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                {additions.map((addition) => {
                  const isApproving =
                    loadingAction === `approve-addition-${addition.id}`;

                  const isRejecting =
                    loadingAction === `reject-addition-${addition.id}`;

                  return (
                    <div
                      key={addition.id}
                      className="rounded-2xl bg-card border border-primary/20 p-5 shadow-[0_2px_8px_rgba(28,25,23,0.04)] hover:shadow-[0_8px_24px_rgba(28,25,23,0.08)] transition-shadow"
                      data-testid={`addition-approval-${addition.id}`}
                    >
                      {/* Header */}
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-display text-lg font-bold">
                              #{addition.order_no}
                            </span>

                            <span className="inline-flex items-center gap-1 rounded-full border border-primary/20 bg-primary/5 text-primary px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide">
                              <PlusCircle size={11} />
                              Add Items
                            </span>
                          </div>

                          <p className="text-xs text-muted-foreground mt-1">
                            {addition.table_name
                              ? `Table ${addition.table_name}`
                              : addition.table_id
                                ? `Table ${addition.table_id}`
                                : "Existing order"}
                          </p>
                        </div>

                        <div className="flex flex-col items-end gap-1.5 shrink-0">
                          <span className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Clock size={12} />
                            {timeAgo(addition.created_at)}
                          </span>

                          <span className="rounded-full bg-amber-50 border border-amber-200 text-amber-700 px-2 py-0.5 text-[10px] font-bold">
                            Pending
                          </span>
                        </div>
                      </div>

                      {/* Addition info */}
                      <div className="mt-3 rounded-xl bg-primary/5 border border-primary/10 px-3 py-2.5">
                        <p className="text-xs font-semibold text-primary">
                          Customer requested additional items
                        </p>

                        <p className="text-[11px] text-muted-foreground mt-0.5">
                          These items will be added to the existing order after
                          approval.
                        </p>
                      </div>

                      {/* New Items */}
                      <div className="mt-3 border border-border rounded-xl divide-y overflow-hidden">
                        {Array.isArray(addition.items) &&
                          addition.items.map((item, index) => (
                            <div
                              key={item.id || item.menu_item_id || index}
                              className="flex items-center gap-2 px-3 py-2 text-sm"
                            >
                              <VegDot
                                veg={item.veg}
                                className="h-3.5 w-3.5 shrink-0"
                              />

                              <span className="flex-1 font-medium leading-tight">
                                {item.name}
                              </span>

                              <span className="text-muted-foreground font-medium">
                                ×{item.qty}
                              </span>

                              {item.price != null && (
                                <span className="font-semibold min-w-[60px] text-right">
                                  {money(
                                    Number(item.price) * Number(item.qty || 1),
                                  )}
                                </span>
                              )}
                            </div>
                          ))}
                      </div>

                      {/* Addition note */}
                      {addition.note && (
                        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-2 mt-2">
                          <span className="font-bold">Note:</span>{" "}
                          {addition.note}
                        </div>
                      )}

                      {/* Addition total */}
                      <div className="flex items-center justify-between mt-4">
                        <div>
                          <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">
                            New Items Total
                          </p>

                          <p className="font-display text-xl font-bold mt-0.5">
                            {money(
                              (addition.items || []).reduce(
                                (sum, item) =>
                                  sum +
                                  Number(item.price || 0) *
                                    Number(item.qty || 1),
                                0,
                              ),
                            )}
                          </p>
                        </div>

                        <span className="text-xs text-muted-foreground">
                          Existing Order #{addition.order_no}
                        </span>
                      </div>

                      {/* Actions */}
                      <div className="grid grid-cols-2 gap-2 mt-4">
                        <Button
                          variant="outline"
                          onClick={() => {
                            setReject({
                              ...addition,
                              kind: "addition",
                            });

                            setReason(REASONS[0]);
                          }}
                          disabled={!user?.can_approve || !!loadingAction}
                          className="h-10 rounded-xl font-semibold text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                          data-testid={`reject-addition-${addition.id}`}
                        >
                          <XCircle size={16} className="mr-1.5" />

                          {isRejecting ? "Rejecting..." : "Reject"}
                        </Button>

                        <Button
                          onClick={() => approveAddition(addition)}
                          disabled={!user?.can_approve || !!loadingAction}
                          className="h-10 rounded-xl font-semibold"
                          data-testid={`approve-addition-${addition.id}`}
                        >
                          {isApproving ? (
                            <>
                              <RefreshCw
                                size={16}
                                className="mr-1.5 animate-spin"
                              />
                              Approving...
                            </>
                          ) : (
                            <>
                              <CheckCircle2 size={16} className="mr-1.5" />
                              Approve
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}
        </div>
      )}

      {/* =====================================================
          REJECT DIALOG
      ====================================================== */}

      <Dialog
        open={!!reject}
        onOpenChange={(value) => {
          if (!value && !loadingAction) {
            setReject(null);
            setReason(REASONS[0]);
          }
        }}
      >
        <DialogContent
          className="bg-white sm:max-w-sm"
          data-testid="reject-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-display">
              {reject?.kind === "addition"
                ? `Reject Additional Items for Order #${reject?.order_no}`
                : `Reject Order #${reject?.order_no}`}
            </DialogTitle>
          </DialogHeader>

          <p className="text-sm text-muted-foreground">
            Select a reason for rejection.
          </p>

          <div className="space-y-1.5 mt-1">
            {REASONS.map((item) => (
              <button
                key={item}
                onClick={() => setReason(item)}
                disabled={!!loadingAction}
                data-testid={`reason-${item.split(" ")[0].toLowerCase()}`}
                className={`w-full text-left rounded-xl border px-3 py-2.5 text-sm font-medium transition-colors disabled:opacity-50 ${
                  reason === item
                    ? "border-primary bg-accent text-primary"
                    : "border-border hover:bg-secondary"
                }`}
              >
                {item}
              </button>
            ))}
          </div>

          <DialogFooter className="mt-2">
            <Button
              variant="outline"
              onClick={() => {
                setReject(null);
                setReason(REASONS[0]);
              }}
              disabled={!!loadingAction}
              className="h-11 rounded-xl"
            >
              Cancel
            </Button>

            <Button
              onClick={doReject}
              disabled={!!loadingAction}
              className="h-11 rounded-xl font-semibold bg-red-600 hover:bg-red-700"
              data-testid="reject-confirm"
            >
              {loadingAction === `reject-addition-${reject?.id}` ||
              loadingAction === `reject-${reject?.id}`
                ? "Rejecting..."
                : "Confirm Rejection"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SummaryCard({ label, value, icon: Icon }) {
  return (
    <div className="rounded-2xl bg-card border border-border p-4 flex items-center justify-between">
      <div>
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">
          {label}
        </p>

        <p className="font-display text-2xl font-bold mt-1">{value}</p>
      </div>

      <div className="h-10 w-10 rounded-xl bg-primary/10 text-primary grid place-items-center">
        <Icon size={18} />
      </div>
    </div>
  );
}
